/**
 * modules.js
 * ----------
 * Modules directeur adosses a des ViewSets DRF, moteurs IA et import.
 *
 * Extrait de `directeurApi.js` (559 lignes, toutes les fonctions ajoutees a la
 * suite). Chaque lot ajoutait ses appels en fin de fichier : deux lots ouverts
 * en meme temps entraient donc systematiquement en conflit sur ce fichier.
 * `directeurApi.js` reste le point d'entree — il re-exporte ces modules — pour
 * qu'aucun ecran n'ait a changer ses imports.
 */
import axiosClient from "../axiosClient";

/* ──────────────────────────────────────────────────────────────
   CRUD des modules directeur adossés à des ViewSets DRF
   (sports, objectifs, pré-affectations, gouvernance, historiques).
   etablissement est posé par la vue (read-only) -> non envoyé.
────────────────────────────────────────────────────────────── */

const rows = (data) =>
  Array.isArray(data) ? data : data && Array.isArray(data.results) ? data.results : [];

function crud(path) {
  return {
    list: async (params = {}) => rows((await axiosClient.get(path, { params })).data),
    create: async (payload) => (await axiosClient.post(path, payload)).data,
    update: async (id, payload) => (await axiosClient.patch(`${path}${id}/`, payload)).data,
    remove: async (id) => {
      await axiosClient.delete(`${path}${id}/`);
    },
  };
}

export const directeurSports = crud("/directeur/sports/");
export const directeurObjectifs = crud("/directeur/objectifs/");
export const directeurPreaffectations = crud("/directeur/preaffectations/");
export const directeurWorkflows = crud("/directeur/workflows/");
export const directeurHistoriques = crud("/directeur/historiques/");

/* Module IA 1 — Homogénéité des classes : simulation de répartition.
   POST /directeur/repartition/simuler/ -> { students, candidates, ... } */
export async function simulerRepartition(payload) {
  const { data } = await axiosClient.post("/directeur/repartition/simuler/", payload);
  return data;
}

/* Persistance + validation de la répartition retenue (backend migration 0120).
   enregistrer -> statut proposee ; valider -> crée/réaffecte les vraies classes. */
export async function enregistrerSimulationRepartition(payload) {
  const { data } = await axiosClient.post("/directeur/repartition/simulations/", payload);
  return data;
}
export async function validerSimulationRepartition(id) {
  const { data } = await axiosClient.post(`/directeur/repartition/simulations/${id}/valider/`, {});
  return data;
}

/* Saisie manuelle d'attributs élèves (sexe / LV2 / PAP) pour alimenter les
   critères de composition. updates: [{ id, sexe?, lv2?, besoin? }]. */
export async function setStudentsAttributs(updates) {
  const { data } = await axiosClient.post("/directeur/students/attributs/", { updates });
  return data;
}

/* Contraintes dures « incompatibilités » entre élèves (composition des classes).
   GET -> { incompatibilites: [...], students: [...] } ; POST { eleve_a, eleve_b, motif? }. */
export async function getIncompatibilites() {
  const { data } = await axiosClient.get("/directeur/incompatibilites/");
  return data;
}
export async function creerIncompatibilite(payload) {
  const { data } = await axiosClient.post("/directeur/incompatibilites/", payload);
  return data;
}
export async function supprimerIncompatibilite(id) {
  await axiosClient.delete(`/directeur/incompatibilites/${id}/`);
}

/* Module IA 2 — Comptes-rendus de conseil de classe : génération du brouillon.
   POST /directeur/conseil/generer/ -> { brouillon, classe, eleves[] } */
export async function genererConseil(payload) {
  const { data } = await axiosClient.post("/directeur/conseil/generer/", payload);
  return data;
}

/* Module IA 3 — Synthèses de profils : fiche-classe + fiches-élèves.
   GET /directeur/profils/classe/<id>/ -> { classe, eleves[] } */
export async function getProfilsClasse(classeId) {
  const { data } = await axiosClient.get(`/directeur/profils/classe/${classeId}/`);
  return data;
}

/* Moteur d'import générique CSV/XLSX (analyze → preview → commit, + undo). */
export async function importAnalyze(file, type = "eleves") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("type", type);
  const { data } = await axiosClient.post("/import/analyze/", fd);
  return data;
}
/* `source` est SOIT un File, SOIT { rows, filename } : une liste
   photographiee n'a pas de fichier reparseable, la reconnaissance a produit
   ses lignes une fois et l'image n'est pas conservee. Les deux chemins
   traversent ensuite exactement les memes validations. */
function corpsImport(source, type, mapping) {
  const fd = new FormData();
  fd.append("type", type);
  fd.append("mapping", JSON.stringify(mapping));
  if (source instanceof File || source instanceof Blob) {
    fd.append("file", source);
  } else {
    fd.append("rows", JSON.stringify(source?.rows || []));
    if (source?.filename) fd.append("filename", source.filename);
  }
  return fd;
}

export async function importPreview(source, type, mapping) {
  const { data } = await axiosClient.post(
    "/import/preview/", corpsImport(source, type, mapping)
  );
  return data;
}
export async function importCommit(source, type, mapping) {
  const { data } = await axiosClient.post(
    "/import/commit/", corpsImport(source, type, mapping)
  );
  return data;
}
export async function importBatches() {
  const { data } = await axiosClient.get("/import/batches/");
  return data;
}
export async function importUndo(batchId) {
  const { data } = await axiosClient.post(`/import/${batchId}/undo/`);
  return data;
}

/** POST /import/photo/ — lit une liste de classe photographiee (OCR local).
 *  Meme forme de retour qu'`importAnalyze`, plus un `avertissement` : la
 *  reconnaissance se trompe, l'ecran doit le dire. */
export async function importPhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axiosClient.post("/import/photo/", fd);
  return data;
}

/** GET /directeur/repartition/projection/ — effectifs de la rentree suivante. */
export async function getProjectionRentree(annee) {
  const { data } = await axiosClient.get("/directeur/repartition/projection/", {
    params: annee ? { annee } : {},
  });
  return data || {};
}

/** GET /directeur/homogeneite/ — cartographie d'un niveau et recommandations.
 *  Les recommandations designent de vrais eleves et annoncent un gain MESURE
 *  cote serveur en simulant le mouvement, pas un effet suppose. */
export async function getHomogeneiteCartographie(niveau) {
  const { data } = await axiosClient.get("/directeur/homogeneite/", {
    params: niveau ? { niveau } : {},
  });
  return data || {};
}
